/**
 * Thin wrapper around the browser's Web Speech API (SpeechRecognition)
 * tailored for the Atlas chat input. Shared between rm-portal and
 * rm-portal-beta so the mic-button UX stays consistent across both.
 *
 * Produces:
 *   - `isSupported`       — true if the browser exposes SpeechRecognition.
 *                            Button is hidden when false.
 *   - `isListening`       — true while the mic is live. Drives the pulsing
 *                            red ring on the mic button.
 *   - `interimTranscript` — text as it's being spoken, updated on every
 *                            provisional result. The parent merges this
 *                            into the draft in real time so the user sees
 *                            the words forming as they speak.
 *   - `start(onFinal)`    — begin listening. `onFinal(text)` fires once
 *                            per finalised utterance with the committed
 *                            transcript (punctuation applied by the
 *                            browser's recognizer).
 *   - `stop()`            — stop listening and discard any pending
 *                            interim. Safe to call when already stopped.
 *   - `error`             — most recent recognizer error, for UI hints
 *                            ("no-speech", "not-allowed", etc.). Cleared
 *                            on next successful start().
 *
 * No framework dependency — callers wrap Vue-side `ref()`s around the
 * fields they need reactivity on.
 */
export function createVoiceInput(refFactory) {
    const Recognition =
        typeof window !== 'undefined'
            ? window.SpeechRecognition || window.webkitSpeechRecognition
            : null;

    const isSupported = refFactory(!!Recognition);
    const isListening = refFactory(false);
    const interimTranscript = refFactory('');
    const error = refFactory(null);

    let recognition = null;
    let onFinal = null;

    function _makeRecognizer() {
        if (!Recognition) return null;
        const r = new Recognition();
        r.continuous = true;
        r.interimResults = true;
        // Default to the browser's locale — works well enough for English
        // dictation on any mainstream setup. If we ever want multi-lingual
        // support we can expose this as an option.
        r.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

        r.onstart = () => {
            isListening.value = true;
            error.value = null;
        };
        r.onend = () => {
            isListening.value = false;
            interimTranscript.value = '';
        };
        r.onerror = (event) => {
            // Common errors: 'no-speech', 'not-allowed' (permission denied),
            // 'aborted' (expected when we call stop()), 'network'.
            // 'aborted' isn't a user-facing error — silently swallow it.
            if (event?.error && event.error !== 'aborted') {
                error.value = event.error;
            }
            isListening.value = false;
        };
        r.onresult = (event) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                const transcript = result[0]?.transcript ?? '';
                if (result.isFinal) {
                    // Commit final utterance via callback rather than
                    // buffering — the caller decides how to merge with
                    // the draft (append, replace, etc.).
                    if (onFinal) onFinal(transcript);
                } else {
                    interim += transcript;
                }
            }
            interimTranscript.value = interim;
        };
        return r;
    }

    function start(finalCallback) {
        if (!Recognition) return;
        if (isListening.value) return;
        onFinal = typeof finalCallback === 'function' ? finalCallback : null;
        interimTranscript.value = '';
        error.value = null;
        try {
            recognition = _makeRecognizer();
            recognition.start();
        } catch (e) {
            // Happens if start() is called too fast after a previous stop.
            error.value = e?.message || 'recognition-failed';
            isListening.value = false;
        }
    }

    function stop() {
        if (!recognition) return;
        try {
            recognition.stop();
        } catch {
            /* already stopped — no-op */
        }
        recognition = null;
    }

    return {
        isSupported,
        isListening,
        interimTranscript,
        error,
        start,
        stop,
    };
}
